import { ApiConfig } from '@ajin/config/api.config';
import { AppService } from '@ajin/services/app.service';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { UserApiService } from '../modules/users/services/user-api.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
	private _authenticated: boolean = false;
	private tokenKey = 'token';
	private url = ApiConfig.url;
	cookieService = inject(CookieService);

	private _activatedRoute = inject(ActivatedRoute);
	_userApiService = inject(UserApiService);

	tempToken: string | null = null;

	constructor(
		private app: AppService,
		private httpClient: HttpClient,
		private _router: Router
	) { }

	get token(): string {
		if (this.sessionLevel === 'strict') {
			return localStorage.getItem(this.tokenKey) ?? '';
		} else {
			return this.cookieService.get(this.tokenKey) ?? '';
		}
	}

	set token(token: string) {
		if (this.sessionLevel === 'strict') {
			localStorage.setItem(this.tokenKey, token);
		} else {
			const expires = new Date();
			expires.setHours(expires.getHours() + 24 * 365 * 10);
			const options = {
				expires: expires,
				path: '/',
				domain: this._getDomainWithoutSubdomain(),
			};

			this.cookieService.set(this.tokenKey, token, options);
		}
	}

	private _getDomainWithoutSubdomain() {
		let baseUrl = window.location.hostname;
		return baseUrl;
	}

	get sessionLevel(): string {
		return localStorage.getItem('session_level');
	}

	set authKey(key: string) {
		if (this.sessionLevel === 'strict') {
			localStorage.setItem('auth_key', key);
		} else {
			const expires = new Date();
			expires.setHours(expires.getHours() + 24 * 365 * 10);
			// expires.setHours(expires.getHours() + 24);
			const options = {
				expires: expires,
				path: '/',
				domain: this._getDomainWithoutSubdomain(),
			};

			this.cookieService.set('auth_key', key, options);
		}
	}

	get authKey(): string {
		if (this.sessionLevel === 'strict') {
			return localStorage.getItem('auth_key') ?? '';
		} else {
			return this.cookieService.get('auth_key') ?? '';
		}
	}

	forgotPassword(params: any): Observable<any> {
		return this.httpClient.post(this.url + 'auth/forgot-password', params);
	}

	resetPassword(params: any): Observable<any> {
		return this.httpClient.post(this.url + 'auth/reset-password', params);
	}

	resetCurrentPassword(params: any): Observable<any> {
		return this.httpClient.post(
			this.url + 'auth/reset-current-password',
			params
		);
	}

	resetCurrentPasswordTempToken(params: any, headers: any): Observable<any> {
		return this.httpClient.post(
			this.url + 'auth/reset-current-password',
			params,
			headers
		);
	}

	signIn(params: any): Observable<any> {
		return new Observable((observer) => {
			params ??= {};
			// params.host = this._getDomainWithoutSubdomain();

			this.httpClient.post(this.url + 'auth/sign-in', params).subscribe({
				next: (result: any) => {
					this.checkUserPasswordStatus(result.token).subscribe({
						next: (pwdResult) => {
							if (pwdResult?.changePassword) {
								this.tempToken = result.token;
								this._router.navigate(['/reset-current-password'], { replaceUrl: true }).then();
								observer.complete();

								return;
							}

							this.token = result.token;
							this._authenticated = true;

							this.app.loadCredential().subscribe({
								next: (result) => {
									observer.next(result);
									observer.complete();
								},
								error: (error) => {
									observer.error(error);
									observer.complete();
								},
							});
						},
						error: (error) => {
							observer.error(error);
							observer.complete();
						},
					});
				},
				error: (error) => {
					observer.error(error);
				},
			});
		});
	}

	signInByToken(token: string): Observable<any> {
		return new Observable((observer) => {
			this.token = token;
			this._authenticated = true;
			this.app.loadCredential().subscribe({
				next: (result) => {
					observer.next(result);
					observer.complete();
				},
				error: (error) => {
					observer.error(error);
					observer.complete();
				},
			});
		});
	}

	signInUsingToken(): Observable<any> {
		return this.httpClient
			.post(this.url + 'auth/sign-in-with-token', {
				accessToken: this.token,
				host: this._getDomainWithoutSubdomain(),
			})
			.pipe(
				catchError(() => of(false)),
				switchMap((response: any) => {
					if (response.accessToken) {
						this.token = response.accessToken;
					}

					this._authenticated = true;

					return of(true);
				})
			);
	}

	clear() {
		localStorage.setItem('session_level', '');
		localStorage.setItem('auth_key', '');
		localStorage.setItem(this.tokenKey, '');

		this.cookieService.deleteAll();
		this.cookieService.deleteAll('/', this._getDomainWithoutSubdomain());

		this.app.credential = null;
		this.app?.clear?.next(true);
		this._authenticated = false;
	}

	signOut() {
		this.clear();
		this._router.navigate(['sign-out'], { replaceUrl: true }).then(() => {
		});
	}

	signUpRequest(params: any): Observable<any> {
		return this.httpClient.post(this.url + 'sign-up', params);
	}

	signUpCheckEmail(params: any): Observable<any> {
		return this.httpClient.post(this.url + 'sign-up/check/user-email', params);
	}

	signUpCheckPhone(params: any): Observable<any> {
		return this.httpClient.post(this.url + 'sign-up/check/user-phone', params);
	}

	signUpCommit(params: any): Observable<any> {
		return this.httpClient.post(this.url + 'sign-up/commit', params);
	}

	getSignUp(key: any): Observable<any> {
		return this.httpClient.get(this.url + 'sign-up/' + key);
	}

	unlockSession(credentials: {
		email: string;
		password: string;
	}): Observable<any> {
		return this.httpClient.post(this.url + 'auth/unlock-session', credentials);
	}

	check(): Observable<boolean> {
		if (this._authenticated) {
			return of(true);
		}

		if (!this.token) {
			return of(false);
		}
		return of(true);
	}

	checkUserPasswordStatus(token: string): Observable<any> {
		if (!token) return;

		const headers = {
			'X-Skip-Auth': 'true',
			Authorization: 'Bearer ' + token,
		};

		return new Observable((observer) => {
			this._userApiService.getPasswordStatus(headers).subscribe({
				next: (result) => {
					observer.next(result);
					observer.complete();
				},
				error: (error: any) => {
					observer.error(error);
					observer.complete();
				},
			});
		});
	}

	setAuthByTempToken(): void {
		if (!this.tempToken)
			return;

		this.token = this.tempToken;
		this._authenticated = true;

		this.tempToken = null;
	}
}
